import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Info, AlertTriangle, AlertOctagon, ExternalLink } from "lucide-react";
import ConfidenceBar from "@/components/ConfidenceBar";

// Shared read-only recommendation card. NO "Apply" buttons, only deep links.

const SEV_META = {
  info:     { tone: "blue",  icon: Info,         label: "Info" },
  warning:  { tone: "amber", icon: AlertTriangle,label: "Warning" },
  critical: { tone: "red",   icon: AlertOctagon, label: "Critical" },
};

const TONES = {
  blue:  "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB]",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red:   "bg-red-50 text-red-700 border-red-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// Maps a "sources used" chip kind to an in-app deep link.
function linkFor(source) {
  if (!source) return null;
  if (source.kind === "rule")     return { to: "/app/rules",     label: "Open Rules" };
  if (source.kind === "policy")   return { to: "/app/policies",  label: "Open Policies" };
  if (source.kind === "kpi")      return { to: "/app/kpis",      label: "Open KPIs" };
  if (source.kind === "skill")    return { to: "/app/skills",    label: "Open Skills" };
  if (source.kind === "pref")     return { to: "/app/employees", label: "Open Employees" };
  return null;
}
function affectedLinkFor(item) {
  if (!item) return null;
  if (item.kind === "date")     return { to: "/app/roster",    label: "Open Rota" };
  if (item.kind === "employee") return { to: "/app/employees", label: "Open Employees" };
  if (item.kind === "category") return { to: "/app/categories", label: "Open Categories" };
  if (item.kind === "team")     return { to: "/app/teams",     label: "Open Teams" };
  return null;
}

export default function RecommendationCard({ rec, testid }) {
  const [open, setOpen] = useState(false);
  const meta = SEV_META[rec.severity] || SEV_META.info;
  const Icon = meta.icon;
  // Deduplicate deep links
  const deepLinks = [];
  const seen = new Set();
  for (const s of (rec.sources || [])) {
    const l = linkFor(s);
    if (l && !seen.has(l.to)) { seen.add(l.to); deepLinks.push(l); }
  }
  for (const a of (rec.affected || [])) {
    const l = affectedLinkFor(a);
    if (l && !seen.has(l.to)) { seen.add(l.to); deepLinks.push(l); }
  }

  return (
    <div data-testid={testid}
         className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 inline-flex items-center text-[10px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[meta.tone]}`}>
          <Icon className="h-3 w-3 mr-1" />{meta.label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-medium text-[#0B1A36]">{rec.title}</div>
            <ConfidenceBar value={rec.confidence}
              testid={`${testid || "rec"}-confidence`} />
          </div>
          <p className="text-sm text-[#4B5773] mt-1">{rec.message}</p>

          <div className="flex flex-wrap gap-1.5 mt-2">
            {(rec.sources || []).map((s, i) => (
              <Chip key={`s-${i}`} tone="slate">{s.kind}: {s.name || s.id}</Chip>
            ))}
            {(rec.affected || []).map((a, i) => (
              <Chip key={`a-${i}`} tone="blue">{a.kind}: {a.name || a.id}</Chip>
            ))}
          </div>

          {(rec.explain || []).length > 0 && (
            <button type="button"
              data-testid={`${testid || "rec"}-explain`}
              onClick={() => setOpen((v) => !v)}
              className="mt-2 inline-flex items-center text-[11px] text-[#2563EB] hover:underline">
              {open ? <ChevronDown className="h-3 w-3 mr-1" />
                    : <ChevronRight className="h-3 w-3 mr-1" />}
              Why? ({rec.explain.length} steps)
            </button>
          )}
          {open && (
            <ol className="mt-2 list-decimal list-inside text-[11px] text-[#4B5773] space-y-0.5">
              {rec.explain.map((step, i) => (<li key={i}>{step}</li>))}
            </ol>
          )}

          {deepLinks.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              {deepLinks.map((l) => (
                <Link key={l.to} to={l.to}
                  data-testid={`${testid || "rec"}-link-${l.to.replace("/app/","")}`}
                  className="inline-flex items-center text-[11px] text-[#2563EB] hover:underline">
                  <ExternalLink className="h-3 w-3 mr-1" />{l.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ tone = "slate", children }) {
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[tone] || TONES.slate}`}>
      {children}
    </span>
  );
}
